import { Observable, Subscription, defer, finalize, of, shareReplay, tap } from 'rxjs';

/** Private, bounded memory cache; never writes scans to browser storage. */
export class DocumentImageCache {
  private entries = new Map<string, { blob: Blob; expires: number }>();
  private pending = new Map<string, Observable<Blob>>();
  private bytes = 0;
  private generation = 0;
  private queue: Array<{ start: () => void; cancel: () => void }> = [];
  private activeLoads = 0;
  private draining = false;
  constructor(private maxBytes = 64 * 1024 * 1024, private maxEntries = 40,
    private ttl = 10 * 60 * 1000) {}
  clear() {
    this.generation++;
    this.entries.clear();
    this.pending.clear();
    this.bytes = 0;
    const queued = this.queue;
    this.queue = [];
    queued.forEach(request => request.cancel());
  }
  get(key: string, load: () => Observable<Blob>): Observable<Blob> {
    return defer(() => {
      for (const [id, entry] of this.entries) {
        if (entry.expires <= Date.now()) this.remove(id);
      }
      const hit = this.entries.get(key);
      if (hit) {
        this.entries.delete(key);
        this.entries.set(key, hit);
        return of(hit.blob);
      }
      const pending = this.pending.get(key);
      if (pending) return pending;
      const generation = this.generation;
      const request = this.schedule(load).pipe(
        tap(blob => {
          if (generation !== this.generation || blob.size > this.maxBytes) return;
          this.remove(key);
          this.entries.set(key, { blob, expires: Date.now() + this.ttl });
          this.bytes += blob.size;
          while (this.bytes > this.maxBytes || this.entries.size > this.maxEntries) {
            this.remove(this.entries.keys().next().value!);
          }
        }),
        finalize(() => { if (this.pending.get(key) === request) this.pending.delete(key); }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
      this.pending.set(key, request);
      return request;
    });
  }
  private schedule(load: () => Observable<Blob>): Observable<Blob> {
    return new Observable<Blob>(subscriber => {
      let source: Subscription | undefined;
      let started = false, starting = false, stopped = false, released = false;
      const release = () => {
        if (released) return;
        released = true;
        this.activeLoads--;
        this.drain();
      };
      const request = {
        start: () => {
          if (subscriber.closed) return;
          started = true;
          starting = true;
          this.activeLoads++;
          source = defer(load).subscribe({
            next: blob => subscriber.next(blob),
            error: error => subscriber.error(error),
            complete: () => subscriber.complete()
          });
          starting = false;
          // Synchronous completion/cancellation can happen before subscribe returns.
          // Release only after the source teardown, just as for an HTTP cancellation.
          if (stopped) {
            source.unsubscribe();
            release();
          }
        },
        cancel: () => subscriber.complete()
      };
      subscriber.add(() => {
        stopped = true;
        if (!started) {
          const index = this.queue.indexOf(request);
          if (index >= 0) this.queue.splice(index, 1);
          this.drain();
        } else if (!starting) {
          source?.unsubscribe();
          release();
        }
      });
      this.queue.push(request);
      this.drain();
    });
  }
  private drain() {
    if (this.draining) return;
    this.draining = true;
    try {
      while (this.activeLoads < 2 && this.queue.length) this.queue.shift()!.start();
    } finally {
      this.draining = false;
    }
  }
  private remove(key: string) {
    const entry = this.entries.get(key);
    if (entry) this.bytes -= entry.blob.size;
    this.entries.delete(key);
  }
}
